"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
// Removed unused Badge import
import { Button } from "@/components/ui/button";
import { RefreshCw, Zap } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";

interface UsageStats {
  daily: Record<string, number>;
  monthly: Record<string, number>;
}

const USAGE_LIMITS = {
  daily: {
    analyzeSleepPatterns: 100,
    generateSleepGoals: 10,
    generateSuggestedPrompts: 30,
  },
  monthly: {
    analyzeSleepPatterns: 500,
    generateSleepGoals: 50,
    generateSuggestedPrompts: 400,
  }
};

const ENDPOINT_LABELS = {
  analyzeSleepPatterns: "Sleep Analysis",
  generateSleepGoals: "Goal Generation",

  generateSuggestedPrompts: "Suggested Prompts",
};

export function UsageStats() {
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user, loading, isSubscribed } = useAuth();
  const { toast } = useToast();

  const limits = USAGE_LIMITS;

  const fetchUsageStats = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.getUsageStats();
      if (response.success && response.data) {
        setUsageStats(response.data);
      } else {
        toast({
          variant: "destructive",
          title: "Failed to load usage stats",
          description: response.error || "Please try again later",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load usage statistics",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch usage stats for subscribed users (not demo users)
    if (user && !loading && isSubscribed) {
      fetchUsageStats();
    }
  }, [user, loading, isSubscribed]);

  const getUsagePercentage = (endpoint: string, period: 'daily' | 'monthly') => {
    const used = usageStats?.[period]?.[endpoint] || 0;
    const limit = limits[period][endpoint as keyof typeof limits.daily];
    return Math.min((used / limit) * 100, 100);
  };

  // getUsageColor function removed - no longer needed

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Usage
          </CardTitle>
          <CardDescription>
            Track your AI usage and limits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Usage
          </CardTitle>
          <CardDescription>
            Track your AI usage and limits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            Please log in to view usage statistics
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show message for demo users instead of usage stats
  if (!isSubscribed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Usage
          </CardTitle>
          <CardDescription>
            Track your AI usage and limits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            Usage statistics are available for subscribed users only
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!usageStats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Usage
          </CardTitle>
          <CardDescription>
            Track your AI usage and limits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              AI Usage
            </CardTitle>
            <CardDescription>
              Track your AI usage and limits
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUsageStats}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Daily Usage */}
        <div>
          <h3 className="font-semibold mb-3">Today's Usage</h3>
          <div className="space-y-3">
            {Object.entries(ENDPOINT_LABELS).map(([endpoint, label]) => {
              const used = usageStats.daily[endpoint] || 0;
              const limit = limits.daily[endpoint as keyof typeof limits.daily];
              const percentage = getUsagePercentage(endpoint, 'daily');

              return (
                <div key={endpoint} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{label}</span>
                    <span className="text-muted-foreground">
                      {used} / {limit}
                    </span>
                  </div>
                  <Progress
                    value={percentage}
                    className="h-2"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly Usage */}
        <div>
          <h3 className="font-semibold mb-3">This Month's Usage</h3>
          <div className="space-y-3">
            {Object.entries(ENDPOINT_LABELS).map(([endpoint, label]) => {
              const used = usageStats.monthly[endpoint] || 0;
              const limit = limits.monthly[endpoint as keyof typeof limits.monthly];
              const percentage = getUsagePercentage(endpoint, 'monthly');

              return (
                <div key={endpoint} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{label}</span>
                    <span className="text-muted-foreground">
                      {used} / {limit}
                    </span>
                  </div>
                  <Progress
                    value={percentage}
                    className="h-2"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
